(() => {
  const selector = [
    '.myproductpage .product__media-wrapper .product__media-list',
    '.myproductpage .product__media-wrapper .thumbnail-list'
  ].join(', ');

  const initializeList = (list) => {
    if (list.dataset.productGalleryCarouselInitialized === 'true') return;
    list.dataset.productGalleryCarouselInitialized = 'true';

    let items = Array.from(list.querySelectorAll('.slider__slide'));
    let currentIndex = 0;
    let scrollFrame = 0;
    let wheelSettleTimer = 0;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartScrollLeft = 0;
    let pointerMoved = false;
    let suppressClick = false;

    const refreshItems = () => {
      items = Array.from(list.querySelectorAll('.slider__slide')).filter((item) => item.clientWidth > 0);
    };

    const clampIndex = (index) => Math.max(0, Math.min(index, items.length - 1));

    const targetLeftFor = (index) => {
      const item = items[clampIndex(index)];
      if (!item) return list.scrollLeft;

      const maximumScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
      if (index <= 0) return 0;
      if (index >= items.length - 1) return maximumScrollLeft;

      const centeredLeft = item.offsetLeft - ((list.clientWidth - item.offsetWidth) / 2);
      return Math.max(0, Math.min(centeredLeft, maximumScrollLeft));
    };

    const updateCurrentItem = () => {
      refreshItems();
      if (!items.length) return;

      const maximumScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
      if (list.scrollLeft <= 1) {
        currentIndex = 0;
        return;
      }
      if (list.scrollLeft >= maximumScrollLeft - 1) {
        currentIndex = items.length - 1;
        return;
      }

      const viewportCenter = list.scrollLeft + (list.clientWidth / 2);
      let closestIndex = 0;
      let closestDistance = Infinity;

      items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
        const distance = Math.abs(itemCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      currentIndex = closestIndex;
    };

    const scrollToItem = (index, behavior = 'smooth') => {
      if (!items.length || list.scrollWidth <= list.clientWidth + 2) return;
      currentIndex = clampIndex(index);
      list.scrollTo({ left: targetLeftFor(currentIndex), behavior });
    };

    if (!list.hasAttribute('tabindex')) list.setAttribute('tabindex', '0');
    list.addEventListener('dragstart', (event) => event.preventDefault());

    list.addEventListener('scroll', () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(updateCurrentItem);
    }, { passive: true });

    list.addEventListener('wheel', (event) => {
      if (list.scrollWidth <= list.clientWidth + 2) return;

      const isHorizontalGesture = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const isShiftWheel = event.shiftKey && Math.abs(event.deltaY) > 0;
      if (!isHorizontalGesture && !isShiftWheel) return;

      event.preventDefault();
      list.classList.add('is-mouse-dragging');
      list.scrollLeft += (isHorizontalGesture ? event.deltaX : event.deltaY) * 1.25;
      window.clearTimeout(wheelSettleTimer);
      wheelSettleTimer = window.setTimeout(() => {
        list.classList.remove('is-mouse-dragging');
        updateCurrentItem();
        scrollToItem(currentIndex);
      }, 110);
    }, { passive: false });

    list.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (list.scrollWidth <= list.clientWidth + 2) return;

      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartScrollLeft = list.scrollLeft;
      pointerMoved = false;
      suppressClick = false;
    });

    list.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const distance = event.clientX - pointerStartX;
      if (!pointerMoved && Math.abs(distance) <= 5) return;

      if (!pointerMoved) {
        pointerMoved = true;
        list.classList.add('is-mouse-dragging');
        list.setPointerCapture(pointerId);
      }

      list.scrollLeft = pointerStartScrollLeft - (distance * 1.35);
      event.preventDefault();
    });

    const finishPointerDrag = (event) => {
      if (event.pointerId !== pointerId) return;

      const releasedPointerId = pointerId;
      pointerId = null;
      if (list.hasPointerCapture(releasedPointerId)) list.releasePointerCapture(releasedPointerId);
      list.classList.remove('is-mouse-dragging');
      suppressClick = pointerMoved;
      updateCurrentItem();
      if (pointerMoved) {
        scrollToItem(currentIndex);
        window.setTimeout(() => {
          suppressClick = false;
        });
      }
    };

    list.addEventListener('pointerup', finishPointerDrag);
    list.addEventListener('pointercancel', finishPointerDrag);
    list.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);

    list.addEventListener('keydown', (event) => {
      if (list.scrollWidth <= list.clientWidth + 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        updateCurrentItem();
        scrollToItem(currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        updateCurrentItem();
        scrollToItem(currentIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        scrollToItem(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        scrollToItem(items.length - 1);
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      refreshItems();
      updateCurrentItem();
    });
    resizeObserver.observe(list);

    refreshItems();
    requestAnimationFrame(updateCurrentItem);
  };

  const initializeWithin = (root = document) => {
    root.querySelectorAll(selector).forEach(initializeList);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(), { once: true });
  } else {
    initializeWithin();
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
